import * as R from 'remeda';
import { createSelector } from 'reselect';
import { activitySelectors } from 'store/data/activity';
import { subZoneSelectors } from 'store/data/subZone';
import { zoneSelectors } from 'store/data/zone';
import Types from 'Types';
import { timeUtility } from 'utility';
import { misc } from 'variables';

// Domain root — Productivity page state lives under state.page.productivity.
const domain = (state: Types.RootState) => state.page.productivity;

const pivotById = createSelector(domain, (domain) => domain.pivotById);

// Selected week/day are returned as LabeledDate so the UI can render the
// formatted name without recomputing it per component.
const selectedWeek = createSelector(domain, (domain) => {
  const label = timeUtility.format(
    domain.selectedWeek,
    'dashboard-pivot-table-cell-header',
  );

  const labeledDate: Time.LabeledDate = { ...domain.selectedWeek, name: label };

  return labeledDate;
});
const selectedDay = createSelector(domain, (domain) => {
  const label = timeUtility.format(
    domain.selectedDay,
    'dashboard-pivot-table-cell-header',
  );

  const labeledDate: Time.LabeledDate = { ...domain.selectedDay, name: label };

  return labeledDate;
});

/**
 * Build the pivot-table structure for the Productivity page.
 *
 * Data.Activity is the single object that knows the full chain of relations:
 * each activity references its parent zone and its child sub-zones. We group
 * activities by zone, then attach sub-zone rows underneath, producing a
 * three-level tree (zone → activity → sub-zone).
 */
const pivotStructure = createSelector(
  zoneSelectors.byId,
  activitySelectors.list,
  subZoneSelectors.byId,

  (zoneById, activities, subZoneById) => {
    const pivotTable: PivotTable.Row[] = [];

    // Group activities by their parent zoneId (fallback bucket for orphans).
    const activitiesByZone = R.pipe(
      activities,
      R.groupBy((activity) => activity.zoneId ?? misc.MISSING),
    );

    // Leaf level: sub-zones under a specific activity.
    function subZoneRows(subZoneIds: string[], ancestorIds: string[]) {
      const rows: PivotTable.Row[] = R.pipe(
        subZoneIds,
        R.map((subZoneId) => {
          // extract subzone
          const subZone = subZoneById[subZoneId.toString()];
          // early return, if not defined
          if (!R.isDefined(subZone)) return;

          const row: PivotTable.Row = {
            id: subZone.id,
            name: `${subZone.code} - ${subZone.name}`,
            ancestorIds,
            indentLevel: 3,
          };

          return row;
        }),
        R.filter(R.isDefined),
        R.sortBy((row) => row.name),
      );
      return rows;
    }

    // Mid level: activities under a specific zone, with optional sub-zone children.
    function activityRows(activities: Data.Activity[], ancestorIds: string[]) {
      const rows: PivotTable.Row[] = R.pipe(
        activities,
        // Activities without sub-zones sort to the top; secondary sort by code.
        R.sortBy<Data.Activity>(
          [(activity) => !R.isDefined(activity.subZoneIds), 'desc'],
          (activity) => activity.code,
        ),
        R.map((activity) => {
          const childRows = R.isDefined(activity.subZoneIds)
            ? subZoneRows(activity.subZoneIds, [...ancestorIds, activity.id])
            : undefined;

          const row: PivotTable.Row = {
            id: activity.id,
            name: `${activity.code} - ${activity.name}`,
            ancestorIds,
            childRows,
            indentLevel: 2,
          };
          return row;
        }),
      );
      return rows;
    }

    // Top level: iterate zones and build their activity subtrees.
    R.pipe(
      activitiesByZone,
      R.forEachObj.indexed((activities, zoneId) => {
        // extract zone

        const zone = zoneById[zoneId.toString()];
        // early return, if not defined
        if (!R.isDefined(zone)) return;

        pivotTable.push({
          id: zone.id,
          name: zone.name,
          ancestorIds: [],
          childRows: activityRows(activities, [zone.id]),
          indentLevel: 1,
        });
      }),
    );

    return pivotTable;
  },
);

// Flatten the tree according to which rows are currently pivoted (expanded).
// Collapsed branches skip their child rows entirely. Each non-root row is
// stamped with a `treePrefix` carrying a single L-shaped connector
// ("└─ ", U+2514 + U+2500) that signals child-to-parent affiliation.
// Each ancestor level adds 3 leading spaces of indent — no vertical
// continuation lines, so siblings read as a clean indented list rather
// than a fully-drawn box tree.
const displayRows = createSelector(
  pivotStructure,
  pivotById,
  (pivotStructure, pivotById) => {
    function recursiveAddRows(
      rows: PivotTable.Row[],
      returnList: PivotTable.Row[],
      depth: number,
    ) {
      rows.forEach((row) => {
        // Connector only — depth-based indent is applied as CSS padding
        // in PivotMarker so the step is in real layout units and not at
        // the mercy of mono-font space widths.

        let treePrefix = '└─ ';
        if (depth === 0) treePrefix = '';
        if (depth > 1) {
          R.times(depth, () => {
            treePrefix = `  ` + treePrefix;
          });
        }

        returnList.push({ ...row, treePrefix });

        const pivotted = R.isTruthy(pivotById[row.id]);
        if (pivotted && row.childRows)
          recursiveAddRows(row.childRows, returnList, depth + 1);
      });
    }

    const returnRows: PivotTable.Row[] = [];

    recursiveAddRows(pivotStructure, returnRows, 0);

    return returnRows;
  },
);

export { displayRows, pivotById, pivotStructure, selectedDay, selectedWeek };
